import React from "react";
import {
  Grid,
  Paper,
  Box,
  Typography,
  Stack,
  TextField,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Button,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import SectionCard from "./SectionCard";
import {
  BLUE,
  BLUE_DARK,
  BLUE_LIGHT,
  BLUE_PALE,
  BORDER,
  ERROR_C,
  INFO_C,
  INK,
  INK_MUTED,
  INK_SOFT,
  SUCCESS,
  WARN,
  fieldSx,
  getFieldSx,
  autocompleteSx,
  numberToWords,
} from "./shared";
import { schemeBaseRateForMode } from "../../../utils/pawnInterestCalculator";
import { useSecretRevealTap, SECRET_TAP_SX } from "../secretRevealTap";

// ─── Loan & Payment Details section (step 3) ─────────────────────────────────
// Presentational block extracted verbatim from PaymentScreen. All loan/interest/
// insurance/split-payment state and handlers stay in the parent and arrive here
// as props (including the parent's local selectSx/labelSx style objects).
const LoanPaymentSection = ({
  stepStateOf,
  openSections,
  toggleSection,
  sectionSummary,
  paymentLocked,
  formData,
  setFormData,
  errors,
  setErrors,
  loanAmountRef,
  calculateInterest,
  calculateInsurance,
  applyLtv,
  schemes,
  handleSchemeChange,
  // Amount-wise interest: the store prices from a ladder of loan-amount bands
  // instead of from a scheme, so there is no scheme to pick and the rate
  // arrives on its own as soon as the amount is typed. See PawnInterestSlabs.
  amountWiseInterest = false,
  applyAmountSlabRate = () => null,
  interestSlabHint = "",
  pawnMode,
  displayInterestRate,
  maskInterest,
  showSecretMark,
  revealInterest,
  onSecretReveal,
  floorsList,
  deductOneMonth,
  setDeductOneMonth,
  pawnSettings,
  splitPayment,
  setSplitPayment,
  getSplitTotal,
  setCashAmount,
  setBankAmount,
  setBankAmount2,
  setSecondBankEnabled,
  setThirdAccountId,
  setUtrNumber,
  setTxnRefNumber,
  setSecondAccountId,
  getUpiAccountId,
  getFilteredAccounts,
  getCashAccountId,
  loadingAccounts,
  nomineeOptions,
  selectedNominee,
  setSelectedNominee,
  setDialogExistingCustomerId,
  setDialogExistingCustomerName,
  setNewCustomerOpen,
  saveButtonRef,
  bankAccounts,
  cashAmount,
  secondBankEnabled,
  handleCash3,
  handleCashAmountChange,
  bankAmount,
  handleBank1_3,
  handleBankAmountChange,
  utrNumber,
  handleAddSecondBank,
  thirdAccountId,
  secondAccountId,
  bankAmount2,
  handleBank2_3,
  txnRefNumber,
  handleRemoveSecondBank,
  selectSx,
  labelSx,
}) => {
  // Handheld counterpart to F2+O: double-tapping the Interest header reveals the
  // true rate. Only armed for an above-cap loan, so normal loans are unaffected.
  const revealTap = useSecretRevealTap(showSecretMark ? onSecretReveal : null);

  return (
    <SectionCard
      icon="💰"
      title="Loan & Payment Details"
      step={3}
      stepState={stepStateOf(3)}
      collapsed={!openSections[3]}
      onToggle={() => toggleSection(3)}
      summary={sectionSummary[3]}
      readOnly={paymentLocked}
    >
      <Grid container spacing={2}>
        {/* Ornament Value, Loan Amount, Interest, Insurance, Pay & Collect By Date */}
        <Grid item xs={12}>
          <Grid container spacing={1.5}>
            {/* ── Valuation and Terms — three side-by-side groups,
                          each stacking its related fields vertically ─────── */}
            {/* ── Group 1: Valuation ── */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: "10px",
                  borderColor: WARN,
                  borderWidth: "1.5px",
                  bgcolor: `${WARN}06`,
                  height: "100%",
                }}
              >
                {/* Group Header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 3,
                      height: 16,
                      bgcolor: BLUE,
                      borderRadius: 2,
                    }}
                  />
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      color: BLUE,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      fontSize: "0.7rem",
                    }}
                  >
                    💎 Valuation
                  </Typography>
                </Box>

                <Stack spacing={1.5}>
                  {/* Two fields in a flex row with equal width */}
                  <Box display="flex">
                    {/* Item Value */}
                    <Box flex={1}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Item Value"
                        value={
                          formData.itemValue
                            ? `₹${Number(formData.itemValue).toLocaleString(
                                "en-IN",
                              )}`
                            : ""
                        }
                        sx={{
                          ...fieldSx,
                          "& .MuiInputBase-input": {
                            color: BLUE_DARK,
                            fontWeight: 700,
                          },
                        }}
                        InputProps={{ readOnly: true }}
                      />
                    </Box>

                    {/* Loan Amount */}
                    <Box flex={1}>
                      <TextField
                        inputRef={loanAmountRef}
                        fullWidth
                        size="small"
                        label="Loan Amount *"
                        type="number"
                        value={formData.loanAmount}
                        error={!!errors.loanAmount}
                        helperText={errors.loanAmount}
                        onChange={(e) => {
                          let newValue = parseFloat(e.target.value) || "";
                          if (newValue < 0) newValue = 0;
                          const itemVal = Math.max(
                            0,
                            parseFloat(formData.itemValue) || 0,
                          );
                          if (newValue > itemVal) {
                            setErrors((p) => ({
                              ...p,
                              loanAmount: "Cannot exceed ornament value",
                            }));
                            newValue = itemVal;
                          } else
                            setErrors((p) => ({
                              ...p,
                              loanAmount: undefined,
                            }));
                          setFormData((p) => ({
                            ...p,
                            loanAmount: newValue,
                          }));
                          // On the amount basis the ladder decides the rate, so
                          // ask it first; it returns null when this store is
                          // month-wise (or has no ladder yet), and then the rate
                          // already in the field stands, exactly as before.
                          if (applyAmountSlabRate(newValue) === null) {
                            const r = Math.max(
                              0,
                              parseFloat(formData.interestRate) || 0,
                            );
                            if (r > 0) calculateInterest(r, newValue);
                          }
                          const insRate =
                            parseFloat(formData.insuranceRate) || 0;
                          if (insRate > 0)
                            calculateInsurance(insRate, newValue);
                        }}
                        sx={getFieldSx(!!errors.loanAmount)}
                        inputProps={{ min: 0, step: "any" }}
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "Minus") {
                            e.preventDefault();
                          }
                        }}
                      />
                      {/* LTV quick‑fill chips */}
                      {parseFloat(formData.itemValue) > 0 && (
                        <Stack
                          direction="row"
                          sx={{
                            mt: 0.5,
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {[60, 70, 75, 80].map((pct) => (
                            <Chip
                              key={pct}
                              label={`${pct}%`}
                              size="small"
                              onClick={() => applyLtv(pct)}
                              sx={{
                                height: 20,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                cursor: "pointer",
                                bgcolor: BLUE_PALE,
                                color: BLUE_DARK,
                                border: `1px solid ${BLUE_LIGHT}`,
                                "&:hover": { bgcolor: BLUE_LIGHT },
                              }}
                            />
                          ))}
                        </Stack>
                      )}
                      {/* Loan amount in words */}
                      {parseFloat(formData.loanAmount) > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: BLUE_DARK,
                            fontStyle: "italic",
                            fontWeight: 600,
                            fontSize: "0.86rem",
                            mt: 0.5,
                          }}
                        >
                          {numberToWords(formData.loanAmount)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* ── Group 2: Interest ── */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: "10px",
                  borderColor: BLUE,
                  borderWidth: "1.5px",
                  bgcolor: `${BLUE}06`,
                  height: "100%",
                }}
              >
                {/* Group Header — doubles as the touch reveal target (see
                    useSecretRevealTap): on handhelds a double tap here flips an
                    above-cap loan between the 1.16% cap and its true rate. */}
                <Box
                  {...revealTap}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mb: 1.5,
                    ...SECRET_TAP_SX,
                  }}
                >
                  <Box
                    sx={{
                      width: 3,
                      height: 16,
                      bgcolor: INFO_C,
                      borderRadius: 2,
                    }}
                  />
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      color: INFO_C,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      fontSize: "0.7rem",
                    }}
                  >
                    📈 Interest
                  </Typography>
                </Box>

                <Stack spacing={1.5}>
                  {/* Scheme Dropdown — only when the store has schemes AND
                      prices from them. An amount-wise store has no scheme to
                      pick: leaving one selectable would save a scheme_id, and
                      every later receipt would then re-price at the scheme rate
                      and quietly override the slab rate. */}
                  <Box display={"flex"} flex={1}>
                    {!amountWiseInterest && schemes && schemes.length > 0 && (
                      <FormControl fullWidth size="small">
                        <InputLabel sx={labelSx}>Scheme</InputLabel>
                        <Select
                          value={formData.scheme}
                          label="Scheme"
                          onChange={(e) => handleSchemeChange(e.target.value)}
                          sx={selectSx}
                        >
                          {schemes?.map((s) => {
                            // Quote the rate this mode will actually charge —
                            // Interest 2 in unofficial mode, Interest otherwise.
                            const rate = schemeBaseRateForMode(
                              s,
                              Number(pawnMode) === 2,
                            );
                            return (
                              <MenuItem key={s.id} value={s.id}>
                                {s.scheme_name}
                                {rate ? ` (${rate}%)` : ""}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    )}

                    {/* Interest % */}
                    <TextField
                      fullWidth
                      size="small"
                      label="Interest %"
                      type="number"
                      value={displayInterestRate}
                      // Which band the amount landed in. Shown so a teller can
                      // see why the rate is what it is without opening Settings.
                      // The field stays editable — the ladder fills it in, it
                      // does not lock it — and re-fills on the next amount change.
                      helperText={
                        amountWiseInterest && !maskInterest
                          ? interestSlabHint
                          : ""
                      }
                      InputProps={{
                        // While masked the true rate is hidden — block
                        // edits so it can't be silently overwritten.
                        readOnly: maskInterest,
                        // Faint secret mark: barely-visible while
                        // masked, turns solid once revealed via F2+O.
                        endAdornment: showSecretMark ? (
                          <InputAdornment position="end">
                            <Box
                              sx={{
                                width: 5,
                                height: 5,
                                borderRadius: "50%",
                                bgcolor: revealInterest
                                  ? ERROR_C
                                  : "rgba(220,38,38,0.16)",
                                transition: "background-color 0.2s ease",
                              }}
                            />
                          </InputAdornment>
                        ) : undefined,
                      }}
                      onChange={(e) => {
                        let value = e.target.value;
                        if (value === "") {
                          setFormData((p) => ({
                            ...p,
                            interestRate: "",
                            scheme: "",
                          }));
                          return;
                        }
                        let num = parseFloat(value);
                        if (!isNaN(num) && num >= 0) {
                          setFormData((p) => ({
                            ...p,
                            interestRate: num,
                            scheme: "",
                          }));
                          calculateInterest(num);
                        } else if (num < 0) {
                          setFormData((p) => ({
                            ...p,
                            interestRate: 0,
                            scheme: "",
                          }));
                          calculateInterest(0);
                        }
                      }}
                      sx={fieldSx}
                      inputProps={{ min: 0, step: "any" }}
                      onKeyDown={(e) => {
                        if (e.key === "-" || e.key === "Minus") {
                          e.preventDefault();
                        }
                      }}
                    />

                    {/* Interest Amount */}
                    <TextField
                      fullWidth
                      size="small"
                      label="Interest Amount"
                      value={
                        formData.interestAmount
                          ? `₹${Number(formData.interestAmount).toLocaleString(
                              "en-IN",
                            )}`
                          : ""
                      }
                      sx={{
                        ...fieldSx,
                        "& .MuiInputBase-input": {
                          color: INK_SOFT,
                        },
                      }}
                      InputProps={{ readOnly: true }}
                    />
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* ── Group 3: Charges & Deductions ── */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: "10px",
                  borderColor: BLUE,
                  borderWidth: "1.5px",
                  bgcolor: `${BLUE}06`,
                  height: "100%",
                }}
              >
                {/* Group Header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 3,
                      height: 16,
                      bgcolor: WARN,
                      borderRadius: 2,
                    }}
                  />
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      color: WARN,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      fontSize: "0.7rem",
                    }}
                  >
                    🧾 Floor and Charges
                  </Typography>
                </Box>

                <Stack spacing={1.5}>
                  {/* Floor / Location selector */}
                  <Box display={"flex"} flex={1}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={labelSx}>Select Floor</InputLabel>
                      <Select
                        value={formData.floorId}
                        label="Select Floor"
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            floorId: e.target.value,
                          }))
                        }
                        sx={selectSx}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {floorsList.map((f) => (
                          <MenuItem key={f.id} value={f.id}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                }}
                              >
                                {f.name}
                              </Typography>
                              {f.location?.name && (
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: 500,
                                    color: "#3D3D3D",
                                    fontStyle: "italic",
                                  }}
                                >
                                  Location - {f.location.name}
                                </Typography>
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      size="small"
                      label="Tare Weight (g)"
                      type="number"
                      value={formData.tareWeight}
                      onChange={(e) => {
                        let v = e.target.value;
                        if (parseFloat(v) < 0) v = "";
                        setFormData((p) => ({
                          ...p,
                          tareWeight: v,
                        }));
                      }}
                      sx={fieldSx}
                      inputProps={{ min: 0, step: "any" }}
                      onKeyDown={(e) => {
                        if (e.key === "-" || e.key === "Minus") {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Box>

                  <Box display={"flex"} flex={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Other Charges"
                      type="number"
                      value={formData.otherCharges}
                      onChange={(e) => {
                        let value = e.target.value;
                        if (value === "") {
                          setFormData((p) => ({
                            ...p,
                            otherCharges: "",
                          }));
                          return;
                        }
                        let num = parseFloat(value);
                        if (!isNaN(num) && num >= 0)
                          setFormData((p) => ({
                            ...p,
                            otherCharges: num,
                          }));
                        else if (num < 0)
                          setFormData((p) => ({
                            ...p,
                            otherCharges: 0,
                          }));
                      }}
                      sx={fieldSx}
                      inputProps={{ min: 0, step: "any" }}
                      onKeyDown={(e) => {
                        if (e.key === "-" || e.key === "Minus")
                          e.preventDefault();
                      }}
                    />

                    {deductOneMonth &&
                      pawnSettings?.master_ins &&
                      parseFloat(pawnSettings.master_ins) > 0 && (
                        <TextField
                          fullWidth
                          size="small"
                          label={`Insurance Amount (${Number(
                            formData.insuranceRate || pawnSettings.master_ins,
                          )}%)`}
                          value={
                            formData.insuranceAmount
                              ? `₹${Number(
                                  formData.insuranceAmount,
                                ).toLocaleString("en-IN")}`
                              : ""
                          }
                          sx={{
                            ...fieldSx,
                            "& .MuiInputBase-input": {
                              color: INK_SOFT,
                            },
                          }}
                          InputProps={{ readOnly: true }}
                        />
                      )}
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* Divider */}
        <Grid item xs={12}>
          <Divider sx={{ borderColor: BORDER }} />
        </Grid>

        {/* Split Payment Toggle + Deduct One Month */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={splitPayment}
                  size="small"
                  onChange={(e) => {
                    setSplitPayment(e.target.checked);
                    if (e.target.checked) {
                      const total = getSplitTotal();
                      setCashAmount(total);
                      setBankAmount("");
                      setBankAmount2("");
                      setSecondBankEnabled(false);
                      setThirdAccountId("");
                      setUtrNumber("");
                      setTxnRefNumber("");
                      // Leg 2 defaults to the configured UPI-transfer
                      // account, else the first non-cash account —
                      // the dropdown stays freely selectable.
                      setSecondAccountId(
                        getUpiAccountId() ||
                          getFilteredAccounts().find(
                            (a) => a.account_id !== getCashAccountId(),
                          )?.account_id ||
                          "",
                      );
                    } else {
                      setCashAmount("");
                      setBankAmount("");
                      setBankAmount2("");
                      setSecondAccountId("");
                      setSecondBankEnabled(false);
                      setThirdAccountId("");
                      setUtrNumber("");
                      setTxnRefNumber("");
                    }
                  }}
                  sx={{
                    color: BLUE,
                    "&.Mui-checked": { color: BLUE_DARK },
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: INK_SOFT }}
                >
                  Split Payment (Cash + Bank)
                </Typography>
              }
            />

            {!!pawnSettings?.one_month_deduct && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={deductOneMonth}
                    size="small"
                    onChange={(e) => setDeductOneMonth(e.target.checked)}
                    sx={{
                      color: BLUE,
                      "&.Mui-checked": { color: BLUE_DARK },
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{ color: INK_SOFT }}
                  >
                    Deduct 1 month interest
                  </Typography>
                }
              />
            )}
          </Box>
        </Grid>

        {/* Payment Section */}
        {!splitPayment ? (
          <Grid item xs={12}>
            <Grid container spacing={2} alignItems="flex-start">
              {/* Payment Account */}
              <Grid item xs={12} sm={6} md={1.75}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={labelSx}>Account</InputLabel>
                  <Select
                    value={formData.payMode || getCashAccountId() || ""}
                    label="Account"
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        payMode: e.target.value,
                      }))
                    }
                    sx={selectSx}
                  >
                    {loadingAccounts ? (
                      <MenuItem disabled>
                        <CircularProgress size={16} /> Loading...
                      </MenuItem>
                    ) : (
                      getFilteredAccounts().map((acc) => (
                        <MenuItem key={acc.account_id} value={acc.account_id}>
                          {acc.account_id === getCashAccountId() ? "💵" : "🏦"}{" "}
                          {acc.account_name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>

              {/* Payment Amount */}
              <Grid item xs={12} sm={6} md={1.25}>
                <TextField
                  fullWidth
                  size="small"
                  label="Amount"
                  type="number"
                  value={formData.paymentAmount}
                  error={!!errors.paymentAmount}
                  helperText={errors.paymentAmount}
                  onChange={(e) => {
                    let value = e.target.value;
                    if (value === "") {
                      setFormData((p) => ({
                        ...p,
                        paymentAmount: "",
                      }));
                      return;
                    }
                    let num = parseFloat(value);
                    if (!isNaN(num) && num >= 0)
                      setFormData((p) => ({
                        ...p,
                        paymentAmount: num,
                      }));
                    else if (num < 0)
                      setFormData((p) => ({
                        ...p,
                        paymentAmount: 0,
                      }));
                  }}
                  InputProps={{
                    readOnly: !!pawnSettings?.one_month_deduct,
                    inputProps: { min: 0, step: "any" },
                  }}
                  sx={{
                    ...getFieldSx(!!errors.paymentAmount),
                    "& .MuiInputBase-input": {
                      fontWeight: 700,
                      color: BLUE,
                    },
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "Minus") e.preventDefault();
                  }}
                />
              </Grid>

              {/* Nominee */}
              <Grid item xs={12} sm={6} md={2}>
                <Autocomplete
                  options={nomineeOptions}
                  getOptionLabel={(o) => o?.name || ""}
                  value={selectedNominee}
                  onChange={(event, newValue) => {
                    if (newValue?.nominee_id === "add_nominee") {
                      setDialogExistingCustomerId(formData.customerId);
                      setDialogExistingCustomerName(formData.customerName);
                      setNewCustomerOpen(true);
                      return;
                    }
                    setSelectedNominee(newValue);
                    setFormData((p) => ({
                      ...p,
                      nominee: newValue ? newValue.name : "",
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Nominee"
                      placeholder="Select nominee"
                      sx={autocompleteSx}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.nominee_id}>
                      <Typography
                        sx={{
                          fontWeight:
                            option.nominee_id === "add_nominee" ? 700 : 400,
                          color:
                            option.nominee_id === "add_nominee" ? BLUE : INK,
                          fontSize: "0.875rem",
                        }}
                      >
                        {option.name}
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(o, v) =>
                    o.nominee_id === v?.nominee_id
                  }
                />
              </Grid>

              {/* Remarks */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Remarks"
                  value={formData.remarks}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      remarks: e.target.value,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (saveButtonRef.current) saveButtonRef.current.focus();
                    }
                  }}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Grid container spacing={2} alignItems="stretch">
              {/* ── Cash Payment Card ── */}
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: "10px",
                    borderColor: SUCCESS,
                    borderWidth: "1.5px",
                    bgcolor: `${SUCCESS}06`,
                  }}
                >
                  {/* Card Header */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 3,
                        height: 16,
                        bgcolor: SUCCESS,
                        borderRadius: 2,
                      }}
                    />
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{
                        color: SUCCESS,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        fontSize: "0.7rem",
                      }}
                    >
                      💵 Cash Payment
                    </Typography>
                  </Box>

                  {/* Cash Account */}
                  <TextField
                    fullWidth
                    size="small"
                    label="Cash Account"
                    value={
                      bankAccounts.find(
                        (a) => a.account_id === getCashAccountId(),
                      )?.account_name || "Cash Account"
                    }
                    InputProps={{ readOnly: true }}
                    sx={{
                      ...fieldSx,
                      mb: 1,
                      "& .MuiInputBase-input": {
                        color: INK_SOFT,
                        fontSize: "0.82rem",
                      },
                    }}
                  />

                  {/* Cash Amount */}
                  <TextField
                    fullWidth
                    size="small"
                    label="Cash Amount"
                    type="number"
                    value={cashAmount}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (secondBankEnabled) {
                        handleCash3(value);
                        return;
                      }
                      if (value === "") {
                        handleCashAmountChange(0);
                        return;
                      }
                      let num = parseFloat(value);
                      if (!isNaN(num) && num >= 0) handleCashAmountChange(num);
                      else if (num < 0) handleCashAmountChange(0);
                    }}
                    sx={{
                      ...getFieldSx(splitPayment && !cashAmount),
                      "& .MuiInputBase-input": {
                        fontWeight: 700,
                        color: SUCCESS,
                      },
                    }}
                    inputProps={{ min: 0, step: "any" }}
                    onKeyDown={(e) => {
                      if (e.key === "-" || e.key === "Minus")
                        e.preventDefault();
                    }}
                  />
                </Paper>
              </Grid>

              {/* ── Bank Transfer Card ── */}
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: "10px",
                    borderColor: INFO_C,
                    borderWidth: "1.5px",
                    bgcolor: `${INFO_C}06`,
                  }}
                >
                  {/* Card Header */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 3,
                        height: 16,
                        bgcolor: INFO_C,
                        borderRadius: 2,
                      }}
                    />
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{
                        color: INFO_C,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        fontSize: "0.7rem",
                      }}
                    >
                      🏦 Account Transfer 1
                    </Typography>
                  </Box>

                  {/* Bank Account 1 — defaults to the configured
                                UPI-transfer account but freely selectable */}
                  <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <InputLabel sx={labelSx}>Bank Account</InputLabel>
                    <Select
                      value={secondAccountId}
                      label="Bank Account"
                      onChange={(e) => setSecondAccountId(e.target.value)}
                      sx={selectSx}
                    >
                      {loadingAccounts ? (
                        <MenuItem disabled>
                          <CircularProgress size={16} />
                        </MenuItem>
                      ) : (
                        getFilteredAccounts()
                          .filter(
                            (a) =>
                              a.account_id !== getCashAccountId() &&
                              a.account_id !== thirdAccountId,
                          )
                          .map((acc) => (
                            <MenuItem
                              key={acc.account_id}
                              value={acc.account_id}
                            >
                              🏦 {acc.account_name}
                            </MenuItem>
                          ))
                      )}
                    </Select>
                  </FormControl>

                  {/* Bank Amount 1 */}
                  <TextField
                    fullWidth
                    size="small"
                    label="Bank Amount"
                    type="number"
                    value={bankAmount}
                    onChange={(e) => {
                      if (secondBankEnabled) handleBank1_3(e.target.value);
                      else
                        handleBankAmountChange(parseFloat(e.target.value) || 0);
                    }}
                    sx={{
                      ...getFieldSx(splitPayment && !bankAmount),
                      "& .MuiInputBase-input": {
                        fontWeight: 700,
                        color: INFO_C,
                      },
                    }}
                    inputProps={{ min: 0, step: "any" }}
                    onKeyDown={(e) => {
                      if (e.key === "-" || e.key === "Minus")
                        e.preventDefault();
                    }}
                  />

                  {/* Transaction Reference Number (leg 1) */}
                  <TextField
                    fullWidth
                    size="small"
                    label="Transaction Reference Number"
                    placeholder="Cheque no / transaction ref"
                    value={utrNumber}
                    onChange={(e) => setUtrNumber(e.target.value)}
                    sx={{ ...fieldSx, mt: 1 }}
                  />

                  {/* Add second account-transfer leg */}
                  {!secondBankEnabled && (
                    <Button
                      size="small"
                      startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                      onClick={handleAddSecondBank}
                      sx={{
                        mt: 1,
                        color: INFO_C,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        textTransform: "none",
                        p: "2px 6px",
                        "&:hover": { bgcolor: `${INFO_C}10` },
                      }}
                    >
                      Add Another
                    </Button>
                  )}
                </Paper>
              </Grid>

              {/* ── Second Bank Transfer Card (optional) ── */}
              {secondBankEnabled && (
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: "10px",
                      borderColor: INFO_C,
                      borderWidth: "1.5px",
                      bgcolor: `${INFO_C}06`,
                    }}
                  >
                    {/* Card Header */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 0.75,
                        mb: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <Box
                          sx={{
                            width: 3,
                            height: 16,
                            bgcolor: INFO_C,
                            borderRadius: 2,
                          }}
                        />
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{
                            color: INFO_C,
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            fontSize: "0.7rem",
                          }}
                        >
                          🏦 Account Transfer 2
                        </Typography>
                      </Box>
                      <Tooltip title="Remove this transfer">
                        <IconButton
                          size="small"
                          onClick={handleRemoveSecondBank}
                          sx={{ p: 0.25, color: INK_MUTED }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Bank Account 2 */}
                    <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                      <InputLabel sx={labelSx}>Bank Account</InputLabel>
                      <Select
                        value={thirdAccountId}
                        label="Bank Account"
                        onChange={(e) => setThirdAccountId(e.target.value)}
                        sx={selectSx}
                      >
                        {loadingAccounts ? (
                          <MenuItem disabled>
                            <CircularProgress size={16} />
                          </MenuItem>
                        ) : (
                          getFilteredAccounts()
                            .filter(
                              (a) =>
                                a.account_id !== getCashAccountId() &&
                                a.account_id !== secondAccountId,
                            )
                            .map((acc) => (
                              <MenuItem
                                key={acc.account_id}
                                value={acc.account_id}
                              >
                                🏦 {acc.account_name}
                              </MenuItem>
                            ))
                        )}
                      </Select>
                    </FormControl>

                    {/* Bank Amount 2 */}
                    <TextField
                      fullWidth
                      size="small"
                      label="Bank Amount"
                      type="number"
                      value={bankAmount2}
                      onChange={(e) => handleBank2_3(e.target.value)}
                      sx={{
                        ...fieldSx,
                        "& .MuiInputBase-input": {
                          fontWeight: 700,
                          color: INFO_C,
                        },
                      }}
                      inputProps={{ min: 0, step: "any" }}
                      onKeyDown={(e) => {
                        if (e.key === "-" || e.key === "Minus")
                          e.preventDefault();
                      }}
                    />

                    {/* Transaction Reference Number (cheque / ref) */}
                    <TextField
                      fullWidth
                      size="small"
                      label="Transaction Reference Number"
                      placeholder="Cheque no / transaction ref"
                      value={txnRefNumber}
                      onChange={(e) => setTxnRefNumber(e.target.value)}
                      sx={{ ...fieldSx, mt: 1 }}
                    />
                  </Paper>
                </Grid>
              )}

              {/* ── Nominee + Remarks + Total indicator ── */}
              <Grid
                item
                xs={12}
                sm={12}
                md={secondBankEnabled ? 3 : 6}
                sx={{ display: "flex" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  {/* Total at top */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      pb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ color: INK, fontSize: "0.82rem" }}
                    >
                      Total: ₹
                      {(
                        Number(cashAmount || 0) +
                        Number(bankAmount || 0) +
                        Number(bankAmount2 || 0)
                      ).toLocaleString("en-IN")}
                    </Typography>
                    {formData.paymentAmount &&
                      Number(cashAmount || 0) +
                        Number(bankAmount || 0) +
                        Number(bankAmount2 || 0) !==
                        Number(formData.paymentAmount) && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: ERROR_C,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.25,
                          }}
                        >
                          ⚠ Expected ₹
                          {Number(formData.paymentAmount).toLocaleString(
                            "en-IN",
                          )}
                        </Typography>
                      )}
                  </Box>

                  {/* Nominee + Remarks pinned to bottom — stacked
                                vertically since the split layout is narrow */}
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={12}>
                      <Autocomplete
                        options={nomineeOptions}
                        getOptionLabel={(o) => o?.name || ""}
                        value={selectedNominee}
                        onChange={(event, newValue) => {
                          if (newValue?.nominee_id === "add_nominee") {
                            setDialogExistingCustomerId(formData.customerId);
                            setDialogExistingCustomerName(
                              formData.customerName,
                            );
                            setNewCustomerOpen(true);
                            return;
                          }
                          setSelectedNominee(newValue);
                          setFormData((p) => ({
                            ...p,
                            nominee: newValue ? newValue.name : "",
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Nominee"
                            placeholder="Select nominee"
                            sx={autocompleteSx}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.nominee_id}>
                            <Typography
                              sx={{
                                fontWeight:
                                  option.nominee_id === "add_nominee"
                                    ? 700
                                    : 400,
                                color:
                                  option.nominee_id === "add_nominee"
                                    ? BLUE
                                    : INK,
                                fontSize: "0.875rem",
                              }}
                            >
                              {option.name}
                            </Typography>
                          </li>
                        )}
                        isOptionEqualToValue={(o, v) =>
                          o.nominee_id === v?.nominee_id
                        }
                      />
                    </Grid>

                    <Grid item xs={12} md={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Remarks"
                        value={formData.remarks}
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            remarks: e.target.value,
                          }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            if (saveButtonRef.current)
                              saveButtonRef.current.focus();
                          }
                        }}
                        sx={fieldSx}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </SectionCard>
  );
};

export default LoanPaymentSection;
